import { defineComponent, PropType } from 'vue'

type Density = 'compact' | 'default' | 'comfortable'
type hideDetails = boolean | 'auto' | undefined

export default defineComponent({
  name: 'DatePickerComponent',

  props: {
    modelValue: { type: [String, Object], default: '' },
    disabled: { type: Boolean, default: false }, // 텍스트 필드 비활성화
    dimed: { type: Boolean, default: false }, // 텍스트 필드 회색
    clearable: { type: Boolean, default: false }, // 텍스트 필드 클리어 버튼
    density: { type: String as PropType<Density>, default: 'compact' }, // 텍스트 필드 크기
    hideDetails: { type: [Boolean, String] as PropType<hideDetails>, default: false }, // 텍스트 필드 상세 표시
    placeholder: { type: String, default: '날짜' }, // 텍스트 필드 플레이스홀더
    range: { type: Boolean, default: false }, // 단일날짜인지 기간인지
    min: { type: String, default: '' }, // 최소 날짜 제한
    max: { type: String, default: '' }, // 최대 날짜 제한
    mode: { type: String, default: 'date' }, // 날짜만인지 시간까지 포함인지 (date | datetime)
    format: { type: String, default: 'YYYY-MM-DD' }, // 날짜 포맷
    noIcon: { type: Boolean, default: false }, // 텍스트 필드 앞에 아이콘 표시 여부
    smallText: { type: Boolean, default: false }, // 텍스트 필드 글자 작게
    required: { type: Boolean, default: false }, // 필수 입력 여부
    suffix: { type: String, default: '' }, // 텍스트 필드 뒤에 붙는 텍스트
    prefix: { type: String, default: '' }, // 텍스트 필드 앞에 붙는 텍스트
    maxRange: { type: Number, default: 0 }, // 기간선택 최대 기간
  },

  emits: ['update:modelValue', 'change', 'click:clear'],

  data() {
    return {
      menu: false, // 달력 온픈
      label: '', // 텍스트 필드에 표시할 날짜
      dirty: false, // 텍스트 필드에 값이 변경되었는지 여부
      requiredRules: [(v) => !!v || '필수 입력입니다.'],
    }
  },

  computed: {
    isRequired() {
      return this.required
    },
    date: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
  },

  watch: {
    date() {
      // mode가 date인 경우 달력을 닫고 시간까지 포함한 경우 달력을 열어둠
      if (this.mode === 'date') {
        this.menu = false
      }

      this.setLabel()
    },
    range() {
      // 텍스트 필드에 값이 변경되었을 때 텍스트 필드에 표시할 날짜를 변경
      this.setLabel()
    },
    modelValue() {
      this.date = this.modelValue
      this.dirty = true
    },
  },

  mounted() {
    // 기간선택일 경우 에러방지를 위해 기본값 설정
    if (this.mode === 'date' && this.range && !this.modelValue) {
      this.date = { start: '', end: '' }
    }

    this.setLabel()
  },

  methods: {
    clear() {
      this.date = ''
      this.$emit('update:modelValue', null)
      this.$emit('click:clear')
    },

    submit() {
      this.menu = false

      // 단일 emit 으로 부모 modelValue 갱신. computed setter(this.date = ...) 를 거치면
      // setter emit 직후 같은 함수 안에서 this.date 를 다시 읽을 때 prop reactive 가
      // 다음 tick 까지 prev 값을 반환해, 뒤이은 emit 이 절단 전 값을 다시 부모에 덮어쓰는 문제 발생.
      let dateToEmit = this.date

      if (this.range && this.date && this.date.start && this.date.end && this.maxRange) {
        // maxRange 이상이면 maxRange 로 제한 (개월 단위)
        if (this.$moment(this.date.end).diff(this.$moment(this.date.start), 'months') >= this.maxRange) {
          this.$store.commit('showSnackbar', {
            text: `${this.maxRange}개월 이상 선택할 수 없어 ${this.maxRange}개월로 설정되었습니다.`,
            type: 'error',
          })

          // maxRange 개월 "미만" 만 허용 — 검증(diff >= maxRange) 과 일관성 유지.
          // 예) maxRange=3 이면 시작 2026-01-01 → 끝 2026-03-31 (정확히 3개월인 04-01 은 검증에서 막힘).
          dateToEmit = {
            start: this.date.start,
            end: this.$moment(this.date.start).add(this.maxRange, 'months').subtract(1, 'days').format('YYYY-MM-DD'),
          }
        }
      }

      this.$emit('update:modelValue', dateToEmit)
      this.$emit('change')
    },

    setLabel() {
      if (this.range) {
        // 기간선택일 경우
        if (this.date && this.date.start && this.date.end) {
          this.label = `${this.filterText(this.date.start)} ~ ${this.filterText(this.date.end)}`
        } else {
          this.label = ''
        }
      } else {
        // 단일날짜일 경우
        this.label = this.date ? `${this.filterText(this.date)}` : ''
      }
    },

    // 오늘 날짜일 경우 '오늘' 표시
    filterText(date: string | object) {
      if (typeof date === 'string') {
        const textArr = date.split(' ')
        if (textArr[0] === this.$moment().format('YYYY-MM-DD')) {
          return '오늘' + (textArr[1] ? ` ${textArr[1]}` : '')
        } else {
          return date
        }
      }
    },

    // 달력 외부 클릭시 달력 닫기
    onClickOutside() {
      this.menu = false
    },
  },
})
